import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  completeMailchimpSetup,
  createMailchimpMergeField,
  getMailchimpFieldMappings,
  updateMailchimpFieldMapping,
} from '../api/fieldMappings';
import type { MailchimpFieldMappingsResponse } from '../types';

interface UseMailchimpMappingsOptions {
  /**
   * Gate the field-mappings fetch. Field mappings only exist once an audience is
   * selected (on_connect creates them), so callers should pass `false` until then —
   * otherwise the query fetches prematurely (no connection/audience yet), caches an
   * empty/404 result, and the UI shows "No field mappings found" even after they're
   * created. Defaults to true to preserve the post-setup manager's behavior.
   */
  enabled?: boolean;
}

export function useMailchimpMappings(
  orgId: number | string | undefined,
  options?: UseMailchimpMappingsOptions
) {
  const queryClient = useQueryClient();

  const mappingsQuery = useQuery<MailchimpFieldMappingsResponse>({
    queryKey: ['mailchimpMappings', orgId],
    queryFn: () => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return getMailchimpFieldMappings(orgId);
    },
    enabled: !!orgId && (options?.enabled ?? true),
  });

  const updateMapping = useMutation({
    mutationFn: ({
      mappingId,
      body,
    }: {
      mappingId: number;
      body: Parameters<typeof updateMailchimpFieldMapping>[2];
    }) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return updateMailchimpFieldMapping(orgId, mappingId, body);
    },
    onSuccess: _data =>
      queryClient.invalidateQueries({ queryKey: ['mailchimpMappings', orgId] }),
  });

  const completeSetup = useMutation({
    mutationFn: (backfill: boolean) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return completeMailchimpSetup(orgId, backfill);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['mailchimpStatus', orgId] }),
  });

  const createMergeField = useMutation({
    mutationFn: (body: Parameters<typeof createMailchimpMergeField>[1]) => {
      if (!orgId) {
        throw new Error('orgId is required');
      }
      return createMailchimpMergeField(orgId, body);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['mailchimpMappings', orgId] }),
  });

  return useMemo(
    () => ({
      mappings: mappingsQuery,
      isLoading: mappingsQuery.isLoading,
      error: mappingsQuery.error,
      updateMapping,
      completeSetup,
      createMergeField,
    }),
    [mappingsQuery, updateMapping, completeSetup, createMergeField]
  );
}
